// LayoutColumns — Portals-3 3-column drill-down layout for Partner Portal.
//
// Outlook/Mail-style: introducers on the left, clients of selected introducer
// in the middle, deal cards for the selected client on the right. The parent
// PartnerPortal page lifts data/fetchers up so toggling the layout doesn't
// re-fetch.
import { useEffect, useMemo, useState } from "react";
import { ChevronRight, Briefcase, Users, FileText, Star, AlertCircle } from "lucide-react";

import { formatCurrency } from "@/lib/formatters";
import { getSubstageLabel } from "@/lib/dealStages";

// Sentinel value used in selectedIntroducerId state for the "My direct
// introductions" pseudo-row. Picked because no real introducer id can
// equal this literal.
const DIRECT_KEY = "__direct__";

function StatusPill({ substage, status }) {
  const sub = (substage || "").toLowerCase();
  let bg = "bg-gray-100 text-gray-700";
  if (sub.includes("settled") || sub === "complete" || sub === "completed") bg = "bg-emerald-100 text-emerald-700";
  else if (sub.includes("settlement") || sub.includes("mortgage_docs")) bg = "bg-amber-100 text-amber-700";
  else if (sub.includes("conditions") || sub.includes("indicative")) bg = "bg-blue-100 text-blue-700";
  else if (sub.includes("assessment") || sub.includes("submitted")) bg = "bg-purple-100 text-purple-700";
  else if (sub.includes("lost") || sub.includes("cancelled") || sub.includes("withdrawn")) bg = "bg-rose-100 text-rose-700";
  const label = getSubstageLabel?.(sub) || sub || status || "—";
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${bg}`}>{label}</span>;
}

function IntroducerListItem({ item, isSelected, isDirect, onClick }) {
  const showSoon = (item.settling_soon_count || 0) > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-3 py-2.5 border-l-2 transition-colors ${
        isSelected
          ? "bg-[#021d49]/5 border-[#bd9a59]"
          : "border-transparent hover:bg-gray-50"
      } ${isDirect ? "border-l-[#bd9a59]" : ""}`}
    >
      <div className="flex items-center gap-2">
        {isDirect && <Star className="w-3.5 h-3.5 text-[#bd9a59] shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[#021d49] truncate">{item.name || "—"}</div>
          <div className="text-[11px] text-gray-500 flex items-center gap-2 mt-0.5">
            <span>{item.active_deal_count || 0} deals</span>
            <span>·</span>
            <span>{formatCurrency(item.pipeline_total || 0)}</span>
          </div>
        </div>
        {showSoon && (
          <span className="shrink-0 inline-flex items-center gap-0.5 text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">
            <AlertCircle className="w-3 h-3" /> {item.settling_soon_count}
          </span>
        )}
      </div>
    </button>
  );
}

export default function LayoutColumns({ dashboard, clientsCache, dealsCache, fetchers }) {
  const { partner = {}, introducers = [], direct_introductions = {}, totals = {} } = dashboard || {};
  // Portals-5 (2026-06-21) — partners who have flipped is_also_introducer
  // off AND have no active direct deals shouldn't see the "My direct
  // introductions" pseudo-row at all. (Previously: shown whenever there
  // was any direct client OR any direct deal, which left an empty row for
  // pure-downline partners.)
  const hasDirectActivity = (direct_introductions.active_deal_count || 0) > 0
    || (direct_introductions.client_count || 0) > 0;
  const showDirect = hasDirectActivity
    && !(
      (direct_introductions.active_deal_count || 0) === 0
      && partner.is_also_introducer === false
    );

  // Restore previous selection from sessionStorage if we just jumped from the
  // accordion's "View all →" affordance. Falls back to the direct pseudo-row,
  // then to the first introducer, then to null.
  const initialIntroducerId = useMemo(() => {
    let stored = null;
    try { stored = sessionStorage.getItem("solara.partner.columns.selectedIntroducer"); }
    catch (_) { /* ignore */ }
    if (stored) {
      try { sessionStorage.removeItem("solara.partner.columns.selectedIntroducer"); }
      catch (_) { /* ignore */ }
      if (stored === DIRECT_KEY && showDirect) return DIRECT_KEY;
      if (introducers.some((i) => i.id === stored)) return stored;
    }
    if (showDirect) return DIRECT_KEY;
    if (introducers.length > 0) return introducers[0].id;
    return null;
  // showDirect / introducers identity changes only when /api/partner/dashboard refetches.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [selectedIntroducerId, setSelectedIntroducerId] = useState(initialIntroducerId);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [dealsLoading, setDealsLoading] = useState(false);

  // Fetch clients when the introducer selection changes.
  useEffect(() => {
    if (!selectedIntroducerId) return;
    let cancelled = false;
    const cacheKey = selectedIntroducerId;
    const cached = clientsCache[cacheKey];
    if (cached) { setSelectedClientId(null); return; }
    setClientsLoading(true);
    setSelectedClientId(null);
    (async () => {
      try {
        if (cacheKey === DIRECT_KEY) await fetchers.fetchDirectClients();
        else await fetchers.fetchIntroducerClients(cacheKey);
      } catch (_) { /* toast already raised */ }
      finally { if (!cancelled) setClientsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [selectedIntroducerId, clientsCache, fetchers]);

  // Fetch deals when the client selection changes.
  useEffect(() => {
    if (!selectedClientId) return;
    let cancelled = false;
    if (dealsCache[selectedClientId]) return;
    setDealsLoading(true);
    (async () => {
      try { await fetchers.fetchClientDeals(selectedClientId); }
      catch (_) { /* toast already raised */ }
      finally { if (!cancelled) setDealsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [selectedClientId, dealsCache, fetchers]);

  const currentClients = selectedIntroducerId ? (clientsCache[selectedIntroducerId]?.clients || []) : [];
  const currentDealsPayload = selectedClientId ? dealsCache[selectedClientId] : null;
  const currentDeals = currentDealsPayload?.deals || [];

  return (
    <div className="max-w-[1400px] mx-auto px-2 sm:px-4 lg:px-6 py-4">
      <div className="grid grid-cols-1 lg:grid-cols-[280px_320px_1fr] gap-3 lg:gap-4 min-h-[70vh]">
        {/* LEFT — Introducer list */}
        <section className="bg-white border border-gray-200 rounded-md overflow-hidden flex flex-col">
          <div className="px-3 py-2.5 border-b border-gray-200 bg-gray-50">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
              <Users className="w-3 h-3" /> Introducers
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {showDirect && (
              <IntroducerListItem
                isDirect
                isSelected={selectedIntroducerId === DIRECT_KEY}
                onClick={() => setSelectedIntroducerId(DIRECT_KEY)}
                item={{
                  name: "My direct introductions",
                  active_deal_count: direct_introductions.active_deal_count || 0,
                  pipeline_total: direct_introductions.pipeline_total || 0,
                  settling_soon_count: 0,
                }}
              />
            )}
            {introducers.map((intr) => (
              <IntroducerListItem
                key={intr.id}
                item={intr}
                isSelected={selectedIntroducerId === intr.id}
                onClick={() => setSelectedIntroducerId(intr.id)}
              />
            ))}
            {introducers.length === 0 && !showDirect && (
              <div className="p-4 text-xs text-gray-500">No introducers yet.</div>
            )}
          </div>
          {/* Totals footer */}
          <div className="border-t border-gray-200 bg-gray-50 px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">All introducers</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className="text-gray-500">Active deals</div>
                <div className="font-semibold text-[#021d49]">{totals.active_deal_count_all || 0}</div>
              </div>
              <div>
                <div className="text-gray-500">Pipeline</div>
                <div className="font-semibold text-[#021d49]">{formatCurrency(totals.pipeline_total_all || 0)}</div>
              </div>
            </div>
          </div>
        </section>

        {/* MIDDLE — Clients */}
        <section className="bg-white border border-gray-200 rounded-md overflow-hidden flex flex-col">
          <div className="px-3 py-2.5 border-b border-gray-200 bg-gray-50">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
              <Briefcase className="w-3 h-3" /> Clients
            </div>
          </div>
          {clientsLoading ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-500">Loading…</div>
          ) : selectedIntroducerId ? (
            <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
              {currentClients.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedClientId(c.id)}
                  className={`w-full text-left px-3 py-2.5 border-l-2 transition-colors ${
                    selectedClientId === c.id ? "bg-[#021d49]/5 border-[#bd9a59]" : "border-transparent hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#021d49] truncate">{c.name || c.company_name || "—"}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        {c.deal_count || 0} deal{(c.deal_count || 0) === 1 ? "" : "s"} · {formatCurrency(c.pipeline_total || 0)}
                      </div>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  </div>
                </button>
              ))}
              {currentClients.length === 0 && (
                <div className="p-4 text-xs text-gray-500">No clients yet for this introducer.</div>
              )}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-xs text-gray-400 p-4">
              Pick an introducer to see their clients.
            </div>
          )}
        </section>

        {/* RIGHT — Deal cards */}
        <section className="bg-white border border-gray-200 rounded-md overflow-hidden flex flex-col">
          <div className="px-3 py-2.5 border-b border-gray-200 bg-gray-50">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
              <FileText className="w-3 h-3" /> Deals
            </div>
          </div>
          {selectedClientId ? (
            dealsLoading ? (
              <div className="flex-1 flex items-center justify-center text-sm text-gray-500">Loading…</div>
            ) : (
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {currentDealsPayload?.client && (
                  <div className="text-xs text-gray-500 mb-1">
                    Deals for <span className="font-medium text-[#021d49]">{currentDealsPayload.client.name}</span>
                  </div>
                )}
                {currentDeals.map((d) => (
                  <div key={d.id} className="border border-gray-200 rounded-md p-3 hover:border-[#bd9a59]/50 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-xs text-gray-500 truncate">
                          {d.application_reference || d.id.slice(0, 8)}
                          {d.company ? ` · ${d.company}` : ""}
                        </div>
                        <div className="text-sm font-semibold text-[#021d49] mt-0.5">
                          {formatCurrency(d.loan_amount || 0)}
                        </div>
                      </div>
                      <StatusPill substage={d.substage} status={d.status} />
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-100 text-[11px] text-[#bd9a59] font-medium">
                      Your share: {formatCurrency(d.your_share_estimate || 0)} est.
                    </div>
                  </div>
                ))}
                {currentDeals.length === 0 && (
                  <div className="text-xs text-gray-500 p-2">No deals for this client.</div>
                )}
              </div>
            )
          ) : (
            <div className="flex-1 flex items-center justify-center text-xs text-gray-400 p-4">
              Pick a client to see their deals.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
